import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
export const API = `${BACKEND_URL}/api`;

// ── In-memory auth state (NEVER persisted to localStorage) ──────────────
// Tokens live in httpOnly cookies — JS cannot and should not read them.
// We only track expiry timing for proactive refresh.
let _authExpiresAt = 0;
let _sessionMarker = '';
let refreshAttemptCount = 0;

/** Record when the current access token expires (from backend `expires_in`). */
export const setAuthExpiry = (expiresInSeconds) => {
  _authExpiresAt = expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;
  _sessionMarker = expiresInSeconds ? String(Date.now()) : '';
  refreshAttemptCount = 0;
};

/** True when the access token cookie is near expiry and should be refreshed. */
const isAuthNearExpiry = () => _authExpiresAt > 0 && _authExpiresAt < Date.now() + 60_000;

// ── Legacy storage cleanup ──────────────────────────────────────────────
// Pre-migration code stored secrets in localStorage. Remove them eagerly.

export const clearAuthStorage = () => {
  try {
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('rt');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('impersonation');
  } catch { /* ignore in SSR / restricted contexts */ }
  _authExpiresAt = 0;
  _sessionMarker = '';
};

// Eagerly purge any leftover secrets from the pre-migration era
clearAuthStorage();

// ── Backward-compat stubs ───────────────────────────────────────────────
// Some consumer code still imports these. They no longer touch localStorage.

/** @deprecated Tokens live in httpOnly cookies. Returns in-memory session marker. */
export const getAccessToken = () => _sessionMarker;

/** @deprecated Tokens live in httpOnly cookies. Updates in-memory marker only. */
export const setAccessToken = (token) => {
  _sessionMarker = token ? String(Date.now()) : '';
};

/** @deprecated Refresh token is in httpOnly cookie. No-op. */
export const setRefreshToken = () => {};

// ── CSRF (readable cookie — not a secret) ───────────────────────────────

export const getCsrfToken = () => {
  const parts = document.cookie.split(';').map((p) => p.trim());
  const match = parts.find((p) => p.startsWith('csrf_token='));
  if (!match) return '';
  return decodeURIComponent(match.split('=').slice(1).join('=') || '');
};

// ── Cookie-based token refresh ──────────────────────────────────────────

export const refreshAccessToken = async () => {
  try {
    const response = await axios.post(`${API}/auth/refresh`, {}, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
    const expiresIn = response.data?.expires_in;
    if (expiresIn) {
      setAuthExpiry(expiresIn);
      return true;
    }
    return null;
  } catch {
    clearAuthStorage();
    return null;
  }
};

// ── Proactive refresh ───────────────────────────────────────────────────

let proactiveRefreshPromise = null;

async function ensureFreshAccessTokenBeforeRequest(config) {
  const url = String(config.url || '');
  if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
    return;
  }
  if (!isAuthNearExpiry()) {
    return;
  }
  if (!proactiveRefreshPromise) {
    proactiveRefreshPromise = refreshAccessToken().finally(() => {
      proactiveRefreshPromise = null;
    });
  }
  const result = await proactiveRefreshPromise;
  if (!result) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Session expired');
  }
}

// ── Axios instance (cookies carry auth automatically) ───────────────────

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ─────────────────────────────────────────────────────────────────────────────
// Global GET caching + request deduplication
// ─────────────────────────────────────────────────────────────────────────────

const apiCache = new Map();
const inFlight = new Map();

const _cacheKeyFor = (url, config) => {
  // Session-scoped so impersonation/admin contexts don't cross-contaminate.
  const csrf = getCsrfToken() || _sessionMarker || '';
  const paramsKey = config?.params ? JSON.stringify(config.params) : '';
  return `${csrf}|${url}|${paramsKey}`;
};

/**
 * cachedGet(url, ttl=30000)
 * cachedGet(url, { ttl=30000, params })
 *
 * Returns response.data (not the Axios response).
 * Deduplicates concurrent requests for the same key.
 */
export const cachedGet = async (url, ttl = 30000, config) => {
  let effectiveTtl = ttl;
  let effectiveConfig = config;

  // Backwards-compatible: cachedGet(url, { ttl, params })
  if (ttl && typeof ttl === 'object') {
    effectiveTtl = ttl.ttl ?? 30000;
    effectiveConfig = ttl.params ? { ...(config || {}), params: ttl.params } : (config || {});
  }

  const key = _cacheKeyFor(url, effectiveConfig);
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.time < effectiveTtl) {
    return cached.data;
  }

  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = api
    .get(url, effectiveConfig)
    .then((res) => {
      apiCache.set(key, { data: res.data, time: Date.now() });
      return res.data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
};

export const clearCache = (url) => {
  if (!url) {
    apiCache.clear();
    inFlight.clear();
    return;
  }

  // Best-effort: remove all cache entries that match the URL for any params.
  for (const key of Array.from(apiCache.keys())) {
    if (key.includes(`|${url}|`)) apiCache.delete(key);
  }
  for (const key of Array.from(inFlight.keys())) {
    if (key.includes(`|${url}|`)) inFlight.delete(key);
  }
};

// ── Request interceptor: CSRF + proactive refresh ───────────────────────

api.interceptors.request.use(async (config) => {
  await ensureFreshAccessTokenBeforeRequest(config);
  config.withCredentials = true;
  const method = String(config.method || '').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// ── Response interceptor: 401 → refresh or impersonation recovery ───────

const MAX_REFRESH_ATTEMPTS = 1;
let refreshInFlight = null;

function runSingleFlightRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      if (refreshAttemptCount >= MAX_REFRESH_ATTEMPTS) {
        return null;
      }
      refreshAttemptCount += 1;
      const result = await refreshAccessToken();
      if (result) {
        refreshAttemptCount = 0;
      }
      return result;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      const url = String(original.url || '');
      if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
        return Promise.reject(error);
      }
      original._retry = true;
      // Impersonation recovery: if impersonating, exit to admin portal
      const isImpersonating = sessionStorage.getItem('is_impersonating');
      if (isImpersonating) {
        sessionStorage.removeItem('is_impersonating');
        try {
          await axios.post(`${API}/admin/demos/end-impersonation`, {}, { withCredentials: true });
        } catch { /* best-effort */ }
        window.location.href = '/admin/demos';
        return Promise.reject(error);
      }
      const result = await runSingleFlightRefresh();
      if (result) {
        return api(original);
      }
      clearAuthStorage();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
